import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { SKETCH_WEBSITE } from '@sketch/env-config'

import {
  Button,
  Link,
  Section,
  useModalContext,
  useStripe,
} from '@sketch/components'

import {
  PlanOverviewPanel,
  BillingHistoryPanel,
  BillingWorkspacePanel,
  PanelSeparator,
  PaymentInformationPanel,
  WorkspaceSettingsSubscribePanel,
  PartnersPanel,
  WorkspaceStoragePanel,
} from '../../components'
import { WorkspaceSettingsUpsellBanner } from '../../components/Upsell'

// Modals
import {
  AddSeatsModal,
  CancelScheduleChangeModal,
  CancelWorkspaceSubscriptionModal,
  ChangeNextBillingCycleModal,
  ReactivateSubscriptionModal,
} from '../../modals'

import {
  BillingSeatsInfoFragment,
  BillingDetailsFragment,
  PaymentDetailsFragment,
  WorkspaceMembershipFragment,
  GetWorkspaceStorageDetailsQuery,
  CloudBillingPlanFragment,
  BillingPriceFragment,
  CreditBalanceFragment,
} from '@sketch/gql-types'

// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus, Currency } from '@sketch/gql-types/expansive'

import {
  isWorkspaceSubscriptionActive,
  isWorkspaceSubscriptionTrialling,
  hasPaymentDetails,
} from '../../utils'
import { dateFormat } from '@sketch/utils'

import {
  PartnersBanner,
  EducationBanner,
} from './WorkspaceSettingsBilling.styles'

interface BillingProps {
  currentPlan?: CloudBillingPlanFragment
  credits: CreditBalanceFragment
  seats: BillingSeatsInfoFragment
  workspaceId: string
  workspaceName: string
  customerId: string
  customerCurrency: Currency
  subscriptionStatus: BillingStatus
  nextBillingCycleDate?: string
  projectedCost?: BillingPriceFragment
  subscriptionEnd?: string
  billingDetails: BillingDetailsFragment
  paymentDetails?: PaymentDetailsFragment
  trialEnd: string
  pendingSCAToken?: string
  isWorkspaceSubscribed?: boolean
  isSsoActive?: boolean
  onWorkspaceSettingRefresh: () => Promise<void>
  isFinance?: boolean
  isPartner?: boolean
  isAdmin: boolean
  partner: WorkspaceMembershipFragment | null
  isEducationWorkspace: boolean
  isEnterpriseWorkspace: boolean
  showUpsell?: boolean
  storageDetails?: GetWorkspaceStorageDetailsQuery
  hasUnlimitedStorage: boolean
}

/**
 *
 * Billing
 *
 * Renders the "Billing" page content in "Workspace Settings"
 */
export const WorkspaceSettingsBilling: React.FCC<BillingProps> = props => {
  const {
    currentPlan,
    credits,
    seats,
    workspaceId,
    workspaceName,
    customerId,
    customerCurrency,
    projectedCost,
    subscriptionStatus,
    subscriptionEnd,
    nextBillingCycleDate,
    billingDetails,
    paymentDetails,
    trialEnd,
    pendingSCAToken,
    isSsoActive,
    onWorkspaceSettingRefresh,
    isFinance,
    isPartner,
    isAdmin,
    showUpsell,
    partner,
    isEducationWorkspace,
    isEnterpriseWorkspace,
    storageDetails,
    hasUnlimitedStorage,
  } = props

  const { hash } = useLocation()

  const { load: loadStripe } = useStripe()
  const { showModal } = useModalContext()

  // Load stripe in order previously
  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  const isWorkspaceOnTrial =
    isWorkspaceSubscriptionTrialling(subscriptionStatus)
  const isSubscriptionActive = isWorkspaceSubscriptionActive(subscriptionStatus)
  const customerHasPaymentDetails = hasPaymentDetails(paymentDetails)

  const isPartneredWorkspace = !!(isPartner || (partner && partner.acceptedAt))
  const isOnTrial = new Date(trialEnd) > new Date()

  const canCancelSubscription =
    isSubscriptionActive &&
    !subscriptionEnd &&
    nextBillingCycleDate &&
    customerHasPaymentDetails &&
    !isFinance &&
    !(isPartneredWorkspace && !isPartner)

  // Handle scroll to Sketch Partner section
  const sketchPartnerSectionRef = useRef<HTMLDivElement | null>(null)
  const handleScrollPosition = () => {
    if (hash === '#sketch-partner') {
      window.requestAnimationFrame(() => {
        if (
          !sketchPartnerSectionRef.current ||
          !sketchPartnerSectionRef.current.scrollIntoView
        )
          return
        sketchPartnerSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    }
  }

  const educationBannerExpireDate = nextBillingCycleDate ?? subscriptionEnd

  const shouldRenderPaymentDetails =
    (isEducationWorkspace && !customerHasPaymentDetails) ||
    (isPartneredWorkspace && !isPartner)
      ? false
      : credits.amount !== 0 ||
        (paymentDetails && paymentDetails.type !== 'NONE')

  const shouldRenderPartnersPanel =
    (!isEducationWorkspace || customerHasPaymentDetails) &&
    !isPartner &&
    !isFinance

  const shouldRenderPlanOverview =
    !isEducationWorkspace || customerHasPaymentDetails

  const shouldRenderPartnerBanner =
    !isPartner && partner && partner.acceptedAt && partner?.user?.name

  const shouldRenderEducationBanner =
    isEducationWorkspace && !customerHasPaymentDetails

  const shouldRenderWorkspaceStorage = !!storageDetails

  const canDeleteLargeFiles = isAdmin

  return (
    <>
      {shouldRenderEducationBanner && (
        <EducationBanner type="information">
          Your Education Workspace is free until{' '}
          {dateFormat(new Date(educationBannerExpireDate!))}. <br />
          <Link href={`${SKETCH_WEBSITE}/support/contact/`} external>
            Get in touch
          </Link>{' '}
          if you have any questions or need for an extension.
        </EducationBanner>
      )}

      {shouldRenderPartnerBanner && (
        <PartnersBanner type="information">
          {partner!.user!.name} (Sketch Partner) manages billing in this
          Workspace.
          <br />
          If you have any questions, please email{' '}
          <Link external href="mailto:partners@sketch.com" isUnderlined>
            partners@sketch.com
          </Link>
        </PartnersBanner>
      )}

      {!customerHasPaymentDetails &&
        !isEducationWorkspace &&
        isWorkspaceOnTrial && (
          <PanelSeparator>
            <WorkspaceSettingsSubscribePanel workspaceId={workspaceId} />
          </PanelSeparator>
        )}

      {isPartner && (
        <PanelSeparator>
          <BillingWorkspacePanel
            workspaceName={workspaceName}
            workspaceId={workspaceId}
          />
        </PanelSeparator>
      )}

      {shouldRenderPlanOverview && (
        <PanelSeparator>
          <PlanOverviewPanel
            workspaceId={workspaceId}
            customerId={customerId}
            customerCurrency={customerCurrency}
            currentPlan={currentPlan}
            seats={seats}
            subscriptionStatus={subscriptionStatus}
            customerHasPaymentDetails={customerHasPaymentDetails}
            subscriptionEnd={subscriptionEnd}
            nextBillingCycleDate={nextBillingCycleDate}
            projectedCost={projectedCost}
            trialEndDate={trialEnd}
            isPartneredWorkspace={isPartneredWorkspace}
            isPartner={isPartner}
            isFinance={isFinance}
            isSsoActive={isSsoActive}
            paymentDetails={paymentDetails}
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
            onReactivateSubscription={() => {
              showModal(ReactivateSubscriptionModal, {
                currentPlan: currentPlan!,
                customerCurrency,
                subscriptionEnd: subscriptionEnd!,
                customerId,
                workspaceId,
                isPartner,
              })
            }}
            onSeatScheduleChange={() => {
              currentPlan &&
                showModal(ChangeNextBillingCycleModal, {
                  customerId,
                  customerCurrency,
                  currentSeats: seats.currentSeatsTotal,
                  scheduledSeats: seats.scheduledSeatsTotal || undefined,
                  workspaceId,
                  variant: isPartner ? 'partner' : 'default',
                  nextBillingCycleDate,
                  isEnterpriseWorkspace,
                })
            }}
            onCancelSeatScheduleChange={() => {
              nextBillingCycleDate &&
                showModal(CancelScheduleChangeModal, {
                  workspaceId,
                  currentSeats: seats.currentSeatsTotal,
                  customerId,
                  currentPlan,
                })
            }}
            onAddSeats={() => {
              showModal(AddSeatsModal, {
                seats,
                workspaceId,
                nextBillingCycleDate,
                customerId,
                isOnTrial,
                paymentDetails: paymentDetails,
                variant: isPartner ? 'partner' : 'default',
                customerCurrency,
              })
            }}
          />
        </PanelSeparator>
      )}

      {showUpsell && (
        <WorkspaceSettingsUpsellBanner
          localStorageKey="upsell-business-billing"
          bannerText="Need greater flexibility in payment methods or terms? Upgrade to our Business plan."
        />
      )}

      {shouldRenderWorkspaceStorage && (
        <PanelSeparator>
          <WorkspaceStoragePanel
            workspaceId={workspaceId}
            storageDetails={storageDetails}
            hasUnlimitedStorage={hasUnlimitedStorage}
            canDeleteLargeFiles={canDeleteLargeFiles}
            isEnterpriseWorkspace={isEnterpriseWorkspace}
          />
        </PanelSeparator>
      )}

      {shouldRenderPaymentDetails && (
        <PanelSeparator>
          <PaymentInformationPanel
            customerCurrency={customerCurrency}
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
            credits={credits}
            customerId={customerId}
            billingDetails={billingDetails}
            paymentDetails={paymentDetails}
            subscriptionStatus={subscriptionStatus}
            pendingSCAToken={pendingSCAToken}
            currentPlan={currentPlan}
            isSsoActive={isSsoActive}
          />
        </PanelSeparator>
      )}
      <PanelSeparator>
        <BillingHistoryPanel
          customerId={customerId}
          workspaceId={workspaceId}
          trialEndDate={trialEnd}
          isWorkspaceOnTrial={isWorkspaceOnTrial}
          cancelAtPeriodEnd={subscriptionEnd}
          customerHasPaymentDetails={customerHasPaymentDetails}
          isEducationWorkspace={isEducationWorkspace}
          nextBillingCycleDate={nextBillingCycleDate}
          onLoadComplete={handleScrollPosition}
          isPartner={isPartner}
          partnerAcceptAt={partner?.acceptedAt}
          partnerName={partner?.user?.name}
        />
      </PanelSeparator>

      {shouldRenderPartnersPanel && (
        <PanelSeparator ref={sketchPartnerSectionRef}>
          <PartnersPanel
            workspaceIdentifier={workspaceId}
            isSubscriptionActive={isSubscriptionActive}
            partner={partner!}
          />
        </PanelSeparator>
      )}

      {canCancelSubscription && (
        <PanelSeparator>
          {/* "data-testid="danger-zone-panel"" is used to keep consistency with the older panels in tests */}
          <Section
            data-testid="danger-zone-panel"
            title="Cancel Subscription"
            text="After canceling, you'll be able to use Sketch until the end of the current billing period."
            action={
              <Button
                onClick={() =>
                  showModal(CancelWorkspaceSubscriptionModal, {
                    customerId,
                    nextBillingCycleDate: nextBillingCycleDate!,
                    workspaceId,
                    isPartner,
                  })
                }
                variant="negative-secondary"
              >
                Cancel Subscription&hellip;
              </Button>
            }
          />
        </PanelSeparator>
      )}
    </>
  )
}
